import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { liteUserId } from 'src/environments/environment';
import { LITE_USER_NAVIGATION_CONFIG } from '../config/lite-user-navigation.config';
import { MEMBER_NAVIGATION_CONFIG } from '../config/member-navigation.config';
import { NON_MEMBER_NAVIGATION_CONFIG } from '../config/non-member-navigation.config';
import { NavigationItem } from '../models/layout.model';
import { MemberOrganizationInfoModel } from '../models/member-organization-info.model';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root',
})
export class LayoutService implements OnDestroy {
  private subscription: Subscription;
  private navigationDrawerStateSubject$ = new BehaviorSubject<boolean>(false);
  navigationDrawerState$ = this.navigationDrawerStateSubject$.asObservable();

  private navigationItemsSubject$ = new BehaviorSubject<NavigationItem[]>([]);
  navigationItem$ = this.navigationItemsSubject$.asObservable();

  constructor(
    @Inject(MEMBER_NAVIGATION_CONFIG) memberNavigationConfig: NavigationItem[],
    @Inject(LITE_USER_NAVIGATION_CONFIG) liteUserNavigationConfig: NavigationItem[],
    @Inject(NON_MEMBER_NAVIGATION_CONFIG) nonMemberNavigationConfig: NavigationItem[],
    private dataService: DataService
  ) {
    this.subscription = combineLatest([this.dataService.selectedOrganizationID$, this.dataService.selectedMemberInfo$])
      .pipe(filter(([organizationID, memberInfo]) => !!organizationID && !!memberInfo))
      .subscribe(([organizationID, memberInfo]) => {
        if (memberInfo && memberInfo.member) {
          this.filterByRolesAndEmitNavigationItems(memberNavigationConfig, memberInfo);
        } else if (memberInfo && !memberInfo.member && organizationID !== liteUserId) {
          this.filterByRolesAndEmitNavigationItems(nonMemberNavigationConfig, memberInfo);
        } else {
          this.filterByRolesAndEmitNavigationItems(liteUserNavigationConfig, memberInfo);
        }
      });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  selectNavigationItem(item?: NavigationItem): void {
    const navigationItems = this.navigationItemsSubject$.getValue();
    this.selectItemAndDeselectOthers(navigationItems, item?.id);
    this.navigationItemsSubject$.next(navigationItems);
  }

  openNavigationDrawer() {
    this.navigationDrawerStateSubject$.next(true);
  }

  closeNavigationDrawer() {
    this.navigationDrawerStateSubject$.next(false);
  }

  /**
   * Traverse the items tree and for each item, sets its 'selected' property to true if it matches the given ID
   * All other items will have their 'selected' property set to false.
   *
   * @param {NavigationItem[]} items - The array of navigation items to traverse.
   * @param {string} id - The ID of the navigation item to select.
   */
  private selectItemAndDeselectOthers(items: NavigationItem[], id: string): void {
    for (const item of items) {
      item.selected = false;

      if (item.id === id) {
        item.selected = true;
        if (item.query && item.children) {
          // If the user clicks on tab, select the first child of the item
          item.children[0].selected = true;
        }
      } else if (item.children) {
        this.selectItemAndDeselectOthers(item.children, id);
        item.selected = item.children.some((child) => child.selected);
      }
    }
  }

  private filterNavItemsByRoles(items: NavigationItem[], roles: string[]): NavigationItem[] {
    if (!roles) return items;

    let filteredItems = items.filter((item) => !item.appliesTo || item.appliesTo.some((role) => roles.includes(role)));

    filteredItems.forEach((item) => {
      if (item.children) {
        item.children = this.filterNavItemsByRoles(item.children, roles);
      }
    });

    return filteredItems;
  }

  private filterByRolesAndEmitNavigationItems(items: NavigationItem[], memberInfo: MemberOrganizationInfoModel) {
    let filteredConfig = this.filterNavItemsByRoles(items, memberInfo.roles);
    this.navigationItemsSubject$.next(filteredConfig);
  }
}
